export interface Forecast {
  monthName: string;
  forecastValue: number;
  monthDate: string;
  isEnabledViewComment: boolean;
  baselineCommentExists: boolean;
  baselineReplyExists: boolean;
  commentReaded:boolean;
  isInvoiced: boolean;
  [key: string]: any;
}

export interface FeeStage {
  feestageID: number;
  feeStageReferenceNumber: string;
  description: string;
  cost: number;
  forecasts: Forecast[];
  isCommentAdded: boolean;
  hasOutstandingComment: boolean;
  outstanding: number;
  outstandingCommentCount: number;
  quantity: number;
  thirdPartyCost: number;
  totalCost: number;
}

export interface Subcategory {
  subcategoryID: number;
  subcategoryName: string;
  feestages: FeeStage[];
}

export interface Category {
  categoryID: number;
  categoryName: string;
  subcategories: Subcategory[];
}

export interface Proposal {
  proposalID: number;
  proposalNumber: string;
  proposalName: string;
  proposalDescription: string;
  categories: Category[];
}

export interface Project {
  projectId: number;
  name: string;
  number: string;
  startDate: Date;
  endDate: Date;
  status: number;
  definiteProposals: Proposal[];
  proposedProposals: Proposal[];
  thridPartyProposals: Proposal[];
}

export const ProjectBaselineStatus = {
  Open: 1,
  Created: 2,
  AwaitingReviewbyCommercialLead: 3,
  ApprovedbyCommercialLead: 4,
  RejectedbyCommercialLead: 5,
  ApprovedbySMT: 6,
  RejectedbySMT: 7,
  NoForecastTracker: 8,
  SkipBL: 9,
  SkipCommercial: 10,
  PartiallyApproved: 11
};

export enum BaselineCommentType {
  PM = 1,
  CommercialLead = 2,
  SMT = 3,
}
export const BaselineCommentTypeLabels: Record<number, string> = {
  [BaselineCommentType.PM]: "PM",
  [BaselineCommentType.CommercialLead]: "CL",
  [BaselineCommentType.SMT]: "SMT",
};

export enum ProjectApprovalAction {
  None = 0,
  Approved = 1,
  Rejected = 2,
}

export interface BaselineProjectApproval {
  baselineID: number;
  isSkipBL: boolean;
  isSkipCommercial: boolean;
  projectID: number;
  commercialLeadId?: number | null;
  commercialLeadStatus?: number | null;
  commercialLeadRemarks?: string | null;
  smtId?: number | null;
  smtStatus?: number | null;
  smtRemarks?: string | null;
  isCommercialLead: boolean;
  commercialLeadUpdatedAt?: Date | null;
}

export const ProjectBaselineDescriptions: Record<number, string> = {
  [ProjectBaselineStatus.Open]: "Open",
  [ProjectBaselineStatus.Created]: "Created",
  [ProjectBaselineStatus.AwaitingReviewbyCommercialLead]: "Waiting for Commercial Lead review",
  [ProjectBaselineStatus.ApprovedbyCommercialLead]: "Approved by Commercial Lead",
  [ProjectBaselineStatus.RejectedbyCommercialLead]: "Rejected by Commercial Lead",
  [ProjectBaselineStatus.ApprovedbySMT]: "Approved by SMT",
  [ProjectBaselineStatus.RejectedbySMT]: "Rejected by SMT",
  [ProjectBaselineStatus.NoForecastTracker]: "No forecast tracker",
  [ProjectBaselineStatus.SkipBL]: "BL Skipped/No BL",
  [ProjectBaselineStatus.SkipCommercial]: "Waiting for SMT review",
  [ProjectBaselineStatus.PartiallyApproved]: "Partially Approved",
};

export interface PreviousBaseline {
  id: number;
  projectManagerName: string;
  projectManagerID: number;
  baseLineDate: string;
  createdDate: string;
  status: number;
  noOfProjects: number;
}

export interface ProjectBaselineResponse {
  projects: Project[];
}

export interface BaselineResponse {
  id: number;
  projectManagerID: number;
  baseLineDate: string;
  createdID: number;
  createdDate: string;
  status: number;
  comments: string;
}

export const statusColors: Record<string, string> = {
  [ProjectBaselineStatus.Open]: "#3B82F6", // blue
  [ProjectBaselineStatus.Created]: "#6366F1", // indigo
  [ProjectBaselineStatus.AwaitingReviewbyCommercialLead]: "#FACC15", // yellow
  [ProjectBaselineStatus.ApprovedbyCommercialLead]: "#16A34A", // green
  [ProjectBaselineStatus.RejectedbyCommercialLead]: "#EF4444", // red
  [ProjectBaselineStatus.ApprovedbySMT]: "#16A34A", // green
  [ProjectBaselineStatus.RejectedbySMT]: "#EF4444", // red
  [ProjectBaselineStatus.NoForecastTracker]: "#F97316", // orange
  [ProjectBaselineStatus.SkipBL]: "gray", // gray
  [ProjectBaselineStatus.SkipCommercial]: "#74f916ff", // light green
  [ProjectBaselineStatus.PartiallyApproved]: "#a3167bff", // magenta
};

// =====================
// For saving updates
// =====================

// Equivalent of C# ProjectBaselineDetail
export interface ProjectBaselineDetail {
  ID?: number;
  BaselineID?: number;
  ProjectID: number;
  ProposalID: number;
  ProposalNumber: string;
  ProposalDescription: string;
  FeeStageReferenceNumber: string;
  CategoryID: number;
  SubCategoryID: number;
  FeeStageID: number;
  BaselineMonth: string; // ISO string
  BaselineValue: number;
  Comments?: string;
  BaseLineDisplayType: number;
  ForeCastOldValue?: number;
  StartDate: Date;
  EndDate: Date;
  Outstanding: number;
}

// Equivalent of C# ProjectBaseline
export interface ProjectBaseline {
  id: number;
  projectManagerID: number;
  baseLineDate: string;
  createdID: number;
  createdDate: string;
  status: number;
  comments: string;
}
export interface ProjectBaselineResponse{
    projectBaseline:ProjectBaseline,
    activeMonthDetails:ActiveMonthDetails
}
export interface ActiveMonthDetails{
  year:number,
  month:number,
  currentLockDate:Date,
  minAllowedDate:Date,
  maxAllowedDate:Date
}


// DashboardProjectBaseLineListViewModel
export interface ProjectBaselineViewModel {
  ProjectId: number;
  Number: string;
  Name: string;
  proposals: Proposal[];
}

export interface DashboardProjectBaseLineListViewModel {
  projects: ProjectBaselineViewModel[];
}

// Request payload for save API
export interface SaveBaselineRequest {
  ProjectBaseline: ProjectBaseline;
  BaselineDetails: BaselineDetailRequest[];
}
export interface BaselineDetailRequest {
  ProjectID: number;
  FeeStageID: number;
  BaselineMonth: string;
  BaselineValue: number;
  Comments: string;
  BaseLineDisplayType: number;
  ForecastValue?: number;
}
export interface SaveBaselineResponse {
  Message: string;
  baselineID: number;
}

export const ChangeDisplayType = {
  DefiniteChanges: 101,
  ProposedChanges: 102,
  ThirdPartyChanges: 103,
};

export interface ProjectManagerDto {
  userID: number;
  fullName: string;
}

export interface DropdownOption {
  text: string;
  value: number | string;
}